import React, { useState } from "react";
import axios from "axios";
import {
  Phone,
  MapPin,
  Clock,
  Mail,
  MessageSquare,
  Calendar,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Spinner,
  Alert,
  Toast,
} from "react-bootstrap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

interface FormData {
  client_name: string;
  client_phone: string;
  client_email: string;
  service_type: string;
  appointment_date: string;
  description: string;
}

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    client_name: "",
    client_phone: "",
    client_email: "",
    service_type: "",
    appointment_date: "",
    description: "",
  });

  const [loading, setLoading] = useState<boolean>(false);

  // Toast state
  const [toastMsg, setToastMsg] = useState<string>("");
  const [toastShow, setToastShow] = useState<boolean>(false);
  const [toastVariant, setToastVariant] = useState<"success" | "danger">("success");

  // Handler para inputs, selects y textarea
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handler del submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      const appointmentData = {
        ...formData,
        appointment_date: new Date(formData.appointment_date).toISOString(),
      };

      await axios.post(`${API}/appointments`, appointmentData);

      setToastVariant("success");
      setToastMsg("¡Cita agendada exitosamente!");
      setToastShow(true);

      setFormData({
        client_name: "",
        client_phone: "",
        client_email: "",
        service_type: "",
        appointment_date: "",
        description: "",
      });
    } catch (error) {
      console.error(error);
      setToastVariant("danger");
      setToastMsg("Error al agendar la cita. Intenta nuevamente.");
      setToastShow(true);
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(
      `Hola! Me gustaría agendar una cita para el mantenimiento de mi motocicleta en YT MotoCenter.`
    );
    const phoneNumber = "50685834427";
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
  };

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().slice(0, 16);

  return (
    <section id="contact" className="position-relative py-5" style={{
      background: "linear-gradient(to bottom right, #111827, #1f2937, #111827)",
      overflow: "hidden"
    }}>
      {/* Background Pattern */}
      <div className="position-absolute w-100 h-100" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f97316' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        opacity: 0.1,
        top: 0,
        left: 0,
        zIndex: 0
      }}></div>

      <Container className="position-relative z-10">
        {/* Header */}
        <div className="text-center mb-5">
          <h2 className="display-5 fw-bold text-white">Contáctanos</h2>
          <p className="lead text-gray-300">
            Agenda tu cita o contáctanos para cualquier consulta sobre el mantenimiento de tu motocicleta
          </p>
        </div>

        <Row className="g-4">
          {/* Información de contacto */}
          <Col lg={6}>
            <Card className="h-100 shadow glass text-white border-0">
              <Card.Body>
                <Card.Title className="d-flex align-items-center mb-4">
                  <MessageSquare className="me-2 custom-icons" />
                  Información de Contacto
                </Card.Title>

                {/* Teléfono */}
                <div className="mb-4 d-flex align-items-start">
                  <div className="iconsContact p-3 rounded flex-shrink-0 me-3">
                    <Phone className="text-white" />
                  </div>
                  <div>
                    <h5 className="fw-semibold">Teléfono / WhatsApp</h5>
                    <p className="text-gray-300">85834427</p>
                  </div>
                </div>

                {/* Dirección */}
                <div className="mb-4 d-flex align-items-start">
                  <div className="iconsContact p-3 rounded flex-shrink-0 me-3">
                    <MapPin className="text-white" />
                  </div>
                  <div>
                    <h5 className="fw-semibold">Dirección</h5>
                    <p className="text-gray-300">San José, Moravia</p>
                    <p className="text-gray-300">San Vicente, Costa Rica</p>
                  </div>
                </div>

                {/* Horarios */}
                <div className="mb-4 d-flex align-items-start">
                  <div className="iconsContact p-3 rounded flex-shrink-0 me-3">
                    <Clock className="text-white" />
                  </div>
                  <div>
                    <h5 className="fw-semibold">Horarios</h5>
                    <p className="text-gray-300">Lunes - Viernes: 8:00 AM - 5:00 PM</p>
                    <p className="text-gray-300">Sábados: 8:00 AM - 4:00 PM</p>
                    <p className="text-gray-300">Domingos: Cerrado</p>
                  </div>
                </div>

                {/* Email */}
                <div className="mb-4 d-flex align-items-start">
                  <div className="iconsContact p-3 rounded flex-shrink-0 me-3">
                    <Mail className="text-white" />
                  </div>
                  <div>
                    <h5 className="fw-semibold">Email</h5>
                    <p className="text-gray-300">ytmotocenter@gmail.com</p>
                  </div>
                </div>

                {/* WhatsApp Button */}
                <div className="mt-4 pt-4 border-top border-gray-600">
                  <Button
                    onClick={openWhatsApp}
                    className="whatsapp-button w-100 d-flex justify-content-center align-items-center gap-2"
                  >
                    <MessageSquare size={18} />
                    Agendar por WhatsApp
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Formulario */}
          <Col lg={6}>
            <Card className="h-100 shadow glass text-white border-0">
              <Card.Body>
                <Card.Title className="d-flex align-items-center mb-4">
                  <Calendar className="me-2 custom-icons" />
                  Agendar Cita
                </Card.Title>

                <Form onSubmit={handleSubmit} className="mb-4">
                  {/* Nombre */}
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Nombre Completo *</Form.Label>
                    <Form.Control
                      type="text"
                      name="client_name"
                      value={formData.client_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Tu nombre completo"
                      className="form-control-dark"
                    />
                  </Form.Group>

                  {/* Teléfono */}
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Teléfono / WhatsApp *</Form.Label>
                    <Form.Control
                      type="tel"
                      name="client_phone"
                      value={formData.client_phone}
                      onChange={handleInputChange}
                      required
                      placeholder="88888888"
                      className="form-control-dark"
                    />
                  </Form.Group>

                  {/* Email */}
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Email (Opcional)</Form.Label>
                    <Form.Control
                      type="email"
                      name="client_email"
                      value={formData.client_email}
                      onChange={handleInputChange}
                      placeholder="tu.email@ejemplo.com"
                      className="form-control-dark"
                    />
                    <Form.Text className="text-light">
                      Para recibir notificaciones del estado de tu servicio
                    </Form.Text>
                  </Form.Group>

                  {/* Tipo de Servicio */}
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Tipo de Servicio *</Form.Label>
                    <Form.Select
                      name="service_type"
                      value={formData.service_type}
                      onChange={handleInputChange}
                      required
                      className="form-control-dark"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="Mantenimiento Preventivo">Mantenimiento Preventivo</option>
                      <option value="Mantenimiento Correctivo">Mantenimiento Correctivo</option>
                      <option value="Revisión de líquidos">Revisión de líquidos</option>
                      <option value="Inspección de frenos">Inspección de frenos</option>
                      <option value="Chequeo de neumáticos">Chequeo de neumáticos</option>
                      <option value="Ajuste de cadena">Ajuste de cadena</option>
                      <option value="Cambio de filtros">Cambio de filtros</option>
                      <option value="Revisión eléctrica">Revisión eléctrica</option>
                      <option value="Otro">Otro</option>
                    </Form.Select>
                  </Form.Group>

                  {/* Fecha */}
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Fecha y Hora Preferida *</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      name="appointment_date"
                      value={formData.appointment_date}
                      onChange={handleInputChange}
                      min={minDate}
                      required
                      className="form-control-dark"
                    />
                  </Form.Group>

                  {/* Descripción */}
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Descripción del Problema (Opcional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Describe los síntomas o problemas..."
                      className="form-control-dark"
                    />
                  </Form.Group>

                  {/* Botón Agendar */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-100 d-flex justify-content-center align-items-center gap-2"
                    style={{ backgroundColor: "#3b82f6", border: "none" }}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Agendando...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={18} />
                        Agendar Cita
                      </>
                    )}
                  </Button>
                </Form>

                {/* Nota */}
                <Alert variant="info" className="alert-note">
                  <AlertCircle size={50} />
                  <div>
                    <strong>Nota importante:</strong> Te contactaremos dentro de las próximas 2 horas para confirmar tu cita. Si proporcionaste tu email, recibirás notificaciones sobre el estado de tu servicio.
                  </div>
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {/* Toast */}
        <Toast
          show={toastShow}
          onClose={() => setToastShow(false)}
          bg={toastVariant}
          delay={3000}
          autohide
          className="position-fixed bottom-0 end-0 m-3"
        >
          <Toast.Body className="text-white">{toastMsg}</Toast.Body>
        </Toast>
      </Container>

    </section>

  );
};

export default Contact;
